/**
 * Killer-flow streaming chunker.
 *
 * The 3-asset Vitalora demo (`runKillerReviewFlow` in
 * `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`)
 * returns the assistant response in one shot — markdown body,
 * `agentSteps`, `contentBlocks`, and `projectData` all land together.
 * The product calls for a theatrical typewriter reveal where the
 * response materialises over ~90 seconds in logical chunks so the
 * prospect feels the reviewers working, before the right-pane
 * `ProjectStatusPane` reveals at the end.
 *
 * This file owns the *what* of that reveal — given the canonical
 * `contentBlocks` + `agentSteps`, produce an ordered list of
 * "frames" each of which is a snapshot of how the message should
 * look at that point in the reveal. The hook
 * (`useKillerFlowStream`) owns the *when*.
 *
 * The output is intentionally framed (not opped) so the consumer
 * can pick any index — the message at frame N is just `frames[N]`,
 * no replay required. Frames are deep-cloned so callers can mutate
 * freely.
 */

import {
  type AskPunttAgentStep,
  type AskPunttContentBlock,
} from "@mg/schemas/src/christo/askPuntt";

export interface KillerFlowFrame {
  /** Snapshot of `contentBlocks` to render at this point in the reveal. */
  contentBlocks: AskPunttContentBlock[];
  /** How many of the original `agentSteps` are visible (slice 0..N). */
  revealedStepCount: number;
  /**
   * High-level kind of work this frame represents — used by the hook
   * to weight the schedule (tool frames cluster early, body frames
   * cluster late so the reveal reads like the agent works → writes).
   */
  kind: "intro" | "tool" | "body";
}

/**
 * Split a markdown string into logical reveal-sized chunks.
 *
 * Rules:
 * - Paragraphs separated by `\n\n` become their own chunks.
 * - List blocks (lines starting with `-`, `*`, or `1.`) are exploded
 *   into one chunk per list item so checkmarks/findings land one at
 *   a time rather than all at once.
 * - Fenced code blocks are kept intact so reveals never split a
 *   ```` ``` ```` boundary mid-stream.
 * - Empty strings produce an empty array.
 */
export function splitMarkdownIntoLogicalChunks(md: string): string[] {
  const trimmed = md.trim();
  if (!trimmed) return [];

  // Pre-pass: extract fenced code blocks as atomic chunks. We split
  // on the fence pair to keep the surrounding prose chunkable.
  const segments: { kind: "prose" | "code"; text: string }[] = [];
  const codeFence = /```[\s\S]*?```/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  while ((match = codeFence.exec(trimmed)) !== null) {
    if (match.index > lastIdx) {
      segments.push({
        kind: "prose",
        text: trimmed.slice(lastIdx, match.index),
      });
    }
    segments.push({ kind: "code", text: match[0] });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < trimmed.length) {
    segments.push({ kind: "prose", text: trimmed.slice(lastIdx) });
  }

  const out: string[] = [];
  for (const seg of segments) {
    if (seg.kind === "code") {
      const codeChunk = seg.text.trim();
      if (codeChunk) out.push(codeChunk);
      continue;
    }
    const paragraphs = seg.text.split(/\n{2,}/);
    for (const p of paragraphs) {
      const para = p.trim();
      if (!para) continue;
      const lines = para.split("\n");
      const firstLine = lines[0]?.trim() ?? "";
      const isList = /^(?:-|\*|\d+\.)\s/.test(firstLine);
      if (isList && lines.length > 1) {
        // Emit each list item as its own chunk. Continuation lines
        // (indented under a list item) stick to their owning item.
        let current = "";
        for (const rawLine of lines) {
          const line = rawLine.trimEnd();
          if (/^(?:-|\*|\d+\.)\s/.test(line.trimStart())) {
            if (current) out.push(current);
            current = line;
          } else if (current) {
            current = `${current}\n${line}`;
          } else {
            current = line;
          }
        }
        if (current) out.push(current);
      } else {
        out.push(para);
      }
    }
  }
  return out;
}

function cloneBlock(block: AskPunttContentBlock): AskPunttContentBlock {
  if (block.type === "text") return { type: "text", content: block.content };
  return {
    type: "tool_activity",
    stepRange: [block.stepRange[0], block.stepRange[1]],
  };
}

function cloneBlocks(blocks: AskPunttContentBlock[]): AskPunttContentBlock[] {
  return blocks.map(cloneBlock);
}

/**
 * Number of `tool_call`/`tool_result` agent steps to reveal per
 * frame inside a `tool_activity` block. Two = one tool pair (the
 * killer flow alternates call/result), which reads as one logical
 * step of reviewer work.
 */
const STEPS_PER_TOOL_FRAME = 2;

/**
 * Plan the frame sequence for a killer-flow message.
 *
 * Frame 0 is the FIRST reveal (intro line). The empty/zero state
 * (nothing visible) is implicit — the consumer renders nothing when
 * `currentFrame < 0`.
 *
 * Returns at minimum one frame; if the inputs are empty the lone
 * frame is the fully-revealed (and trivially empty) message.
 */
export function planKillerFlowFrames(
  agentSteps: AskPunttAgentStep[] | undefined,
  contentBlocks: AskPunttContentBlock[] | null | undefined,
): KillerFlowFrame[] {
  const blocks = contentBlocks ?? [];
  const stepCount = agentSteps?.length ?? 0;
  const frames: KillerFlowFrame[] = [];

  if (blocks.length === 0 && stepCount === 0) {
    return [
      {
        contentBlocks: [],
        revealedStepCount: 0,
        kind: "body",
      },
    ];
  }

  // Walking state: blocks revealed so far. Each frame is a clone.
  const working: AskPunttContentBlock[] = [];
  let revealedStepCount = 0;

  // Classify which text blocks are "intro" vs "body". The killer
  // flow always emits [intro text, tool_activity, summary text],
  // so the first text block is intro and any subsequent text block
  // is body.
  let textBlockIdx = 0;

  for (let bIdx = 0; bIdx < blocks.length; bIdx++) {
    const block = blocks[bIdx];
    if (block.type === "text") {
      const isIntro = textBlockIdx === 0;
      textBlockIdx++;
      const parts = splitMarkdownIntoLogicalChunks(block.content);
      if (parts.length === 0) {
        // Empty text block — emit it as a no-op placeholder so
        // later block indices line up with the original layout.
        working.push({ type: "text", content: "" });
        continue;
      }
      let acc = "";
      let lastWasList = false;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isList = /^(?:-|\*|\d+\.)\s/.test(part);
        if (acc) {
          // Preserve the original separator semantics: consecutive
          // list items reassemble with a single newline (so the
          // markdown renderer keeps them in one list), everything
          // else uses a paragraph break.
          const sep = isList && lastWasList ? "\n" : "\n\n";
          acc = `${acc}${sep}${part}`;
        } else {
          acc = part;
        }
        lastWasList = isList;
        // Snapshot = working (blocks 0..bIdx-1) + the in-progress
        // text block at bIdx. We always APPEND the working text
        // block to the cloned snapshot rather than mutating the
        // copy of `working` — `working` represents prior blocks
        // and must stay untouched here.
        const snapshot = cloneBlocks(working);
        snapshot.push({ type: "text", content: acc });
        frames.push({
          contentBlocks: snapshot,
          revealedStepCount,
          kind: isIntro ? "intro" : "body",
        });
      }
      // Commit the fully-revealed text block to the working state
      // so subsequent blocks build on top of it.
      working.push({ type: "text", content: acc });
    } else if (block.type === "tool_activity") {
      const [start, end] = block.stepRange;
      if (end <= start) {
        working.push({
          type: "tool_activity",
          stepRange: [start, end],
        });
        continue;
      }
      let cur = start;
      while (cur < end) {
        const next = Math.min(cur + STEPS_PER_TOOL_FRAME, end);
        const snapshot = cloneBlocks(working);
        snapshot.push({
          type: "tool_activity",
          stepRange: [start, next],
        });
        revealedStepCount = Math.max(revealedStepCount, next);
        frames.push({
          contentBlocks: snapshot,
          revealedStepCount,
          kind: "tool",
        });
        cur = next;
      }
      working.push({ type: "tool_activity", stepRange: [start, end] });
    }
  }

  if (frames.length === 0) {
    frames.push({
      contentBlocks: cloneBlocks(blocks),
      revealedStepCount: stepCount,
      kind: "body",
    });
  }

  return frames;
}

/**
 * Compute target timestamps (ms-from-start) for each frame in the
 * reveal, given the requested total duration. The schedule layers
 * three time windows so the reveal reads as "agent works → agent
 * writes up findings":
 *
 *   intro frames  → first 4–5s (gentle warm-up)
 *   tool frames   → 5s through ~55% of the total (busy/clustered)
 *   body frames   → ~55% through `totalDurationMs` (slower, reading pace)
 *
 * Within each window, frames are distributed linearly and then
 * receive a small jitter so reveals don't tick on a metronome.
 * Jitter is seeded by `messageIdSeed` so the schedule is stable
 * across re-renders for the same message.
 *
 * The returned timings are guaranteed to be strictly increasing
 * (minimum 250ms between successive frames) so chained `setTimeout`
 * never inverts.
 */
export function planKillerFlowTimings(
  frames: KillerFlowFrame[],
  options: {
    totalDurationMs?: number;
    messageIdSeed?: string;
  } = {},
): number[] {
  const totalDurationMs = options.totalDurationMs ?? 88_000;
  const seed = options.messageIdSeed ?? "killer";
  const rng = mulberry32(hashStringToSeed(seed));

  const n = frames.length;
  if (n === 0) return [];

  const introCount = frames.filter((f) => f.kind === "intro").length;
  const toolCount = frames.filter((f) => f.kind === "tool").length;
  const bodyCount = frames.filter((f) => f.kind === "body").length;

  const firstDelay = 2_500;
  const toolWindowStart = introCount > 0 ? 5_000 : firstDelay;
  const toolWindowEnd = Math.round(totalDurationMs * 0.55);
  const bodyWindowStart = Math.min(
    toolWindowEnd + 2_000,
    Math.round(totalDurationMs * 0.6),
  );
  const bodyWindowEnd = totalDurationMs;

  const introWindow: [number, number] = [
    firstDelay,
    Math.min(4_500, toolWindowStart),
  ];
  const toolWindow: [number, number] = [toolWindowStart, toolWindowEnd];
  const bodyWindow: [number, number] = [bodyWindowStart, bodyWindowEnd];

  const pick = (
    window: [number, number],
    idx: number,
    count: number,
  ): number => {
    if (count <= 0) return window[0];
    const [lo, hi] = window;
    if (count === 1) return Math.round((lo + hi) / 2);
    return Math.round(lo + ((hi - lo) * idx) / (count - 1));
  };

  let introIdx = 0;
  let toolIdx = 0;
  let bodyIdx = 0;
  const targets: number[] = [];
  for (const f of frames) {
    let t: number;
    let windowSize: number;
    if (f.kind === "intro") {
      t = pick(introWindow, introIdx, introCount);
      windowSize = introWindow[1] - introWindow[0];
      introIdx++;
    } else if (f.kind === "tool") {
      t = pick(toolWindow, toolIdx, toolCount);
      windowSize = (toolWindow[1] - toolWindow[0]) / Math.max(1, toolCount);
      toolIdx++;
    } else {
      t = pick(bodyWindow, bodyIdx, bodyCount);
      windowSize = (bodyWindow[1] - bodyWindow[0]) / Math.max(1, bodyCount);
      bodyIdx++;
    }
    const jitterRange = Math.max(300, Math.min(2_500, windowSize * 0.25));
    const jitter = Math.round((rng() - 0.5) * 2 * jitterRange);
    targets.push(Math.max(0, t + jitter));
  }

  // Enforce monotonic + minimum spacing.
  for (let i = 1; i < targets.length; i++) {
    if (targets[i] <= targets[i - 1] + 250) {
      targets[i] = targets[i - 1] + 250;
    }
  }
  // Clamp the final frame to <= totalDurationMs. If jitter pushed
  // any frame past the deadline, walk backwards keeping the
  // monotonic gap.
  if (targets[targets.length - 1] > totalDurationMs) {
    targets[targets.length - 1] = totalDurationMs;
    for (let i = targets.length - 2; i >= 0; i--) {
      if (targets[i] >= targets[i + 1] - 250) {
        targets[i] = targets[i + 1] - 250;
      } else {
        break;
      }
    }
  }

  return targets;
}

/**
 * xorshift-based hash from a string to a 32-bit integer seed. Used
 * to make the jitter schedule stable per message-id so re-renders
 * never resequence partway through.
 */
function hashStringToSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h || 1;
}

/**
 * Tiny deterministic PRNG (mulberry32). Returns a function that
 * yields floats in [0, 1). Identical seed → identical sequence,
 * which is what we need for stable jitter.
 */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
